import { useEffect, useRef } from "react";
import type { PageIndicator, DocumentPage, DocumentUser } from "@shared/schema";

interface UseCanvasInteractionEffectsProps {
  pageIndicators: PageIndicator[];
  redraw?: () => void;
  activeIndicator: PageIndicator | null;
  pendingIndicator: {
    x: number;
    y: number;
    canvasX: number;
    canvasY: number;
    number: number;
  } | null;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  getCurrentCursor?: () => string;
  setCurrentCursor: (cursor: string) => void;
  handlePointerClick?: () => { x: number; y: number } | null;
  getIndicatorAtPosition: (x: number, y: number) => PageIndicator | null;
  imageToCanvasCoords: (x: number, y: number) => { x: number; y: number };
  canvasToImageCoords: (x: number, y: number) => { x: number; y: number };
  setActiveIndicator: (indicator: PageIndicator | null) => void;
  setPopoverPosition: (position: { x: number; y: number } | null) => void;
  viewMode: "comment" | "browse";
  dragJustCompleted: boolean;
  setDragJustCompleted: (completed: boolean) => void;
  isDraggingIndicator: () => boolean;
  setPendingIndicator: (indicator: {
    x: number;
    y: number;
    canvasX: number;
    canvasY: number;
    number: number;
  } | null) => void;
  onIndicatorChange: (indicatorNumber: number | null) => void;
  // Version restriction props
  currentVersion?: number;
  latestVersion?: number;
  onVersionRestricted?: () => void;
}

export function useCanvasInteractionEffects({
  pageIndicators,
  redraw,
  activeIndicator,
  pendingIndicator,
  textareaRef,
  getCurrentCursor,
  setCurrentCursor,
  handlePointerClick,
  getIndicatorAtPosition,
  imageToCanvasCoords,
  canvasToImageCoords,
  setActiveIndicator,
  setPopoverPosition,
  viewMode,
  dragJustCompleted,
  setDragJustCompleted,
  isDraggingIndicator,
  setPendingIndicator,
  onIndicatorChange,
  currentVersion,
  latestVersion,
  onVersionRestricted,
}: UseCanvasInteractionEffectsProps) {

  // Force canvas redraw when indicators change (fixes homepage navigation issue)  
  useEffect(() => {
    if (pageIndicators.length > 0 && redraw) {
      // Force redraw when indicators are available
      redraw();
    }
  }, [pageIndicators, redraw]);

  // Manual cursor positioning - fixed client-side approach
  useEffect(() => {
    if ((activeIndicator || pendingIndicator) && typeof window !== 'undefined') {
      const forceCursorPosition = () => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          const textLength = textarea.value.length;
          textarea.setSelectionRange(textLength, textLength);
          textarea.scrollTop = textarea.scrollHeight;
        }
      };

      // Multiple attempts with increasing delays
      setTimeout(forceCursorPosition, 100);
      setTimeout(forceCursorPosition, 300);
      setTimeout(forceCursorPosition, 500);
    }
  }, [activeIndicator, pendingIndicator]);

  // Update cursor state based on canvas viewer state
  useEffect(() => {
    if (getCurrentCursor) {
      const cursor = getCurrentCursor();
      setCurrentCursor(cursor);
    }
  }, [getCurrentCursor]);

  // Set up interval to update cursor state regularly
  useEffect(() => {
    if (!getCurrentCursor) return;
    
    const updateCursor = () => {
      const cursor = getCurrentCursor();
      setCurrentCursor(cursor);
    };

    const interval = setInterval(updateCursor, 50); // Update every 50ms
    return () => clearInterval(interval);
  }, [getCurrentCursor]);

  // Handle pointer clicks for comment functionality
  useEffect(() => {
    if (!handlePointerClick) return;
    
    const checkPointerClick = () => {
      const clickPosition = handlePointerClick();
      if (!clickPosition) return;
      
      // handlePointerClick returns IMAGE coordinates
      const imageX = clickPosition.x;
      const imageY = clickPosition.y;
      
      // Convert IMAGE → VIEWPORT for hit detection
      const viewportCoords = imageToCanvasCoords(imageX, imageY);
      
      // Check if clicked on an existing indicator first (works in both modes)
      const indicator = getIndicatorAtPosition(viewportCoords.x, viewportCoords.y);
      if (indicator) {
        setActiveIndicator(indicator);
        const indicatorCanvasPos = imageToCanvasCoords(indicator.x, indicator.y);
        setPopoverPosition(indicatorCanvasPos);
        // Update URL with indicator number
        if (onIndicatorChange) {
          onIndicatorChange(indicator.number);
        }
        return;
      }
      
      // BROWSE MODE: Prevent new indicator creation, only allow viewing existing ones
      if (viewMode === "browse") {
        return;
      }
      
      // BLOCK ALL indicator creation if drag just completed or popover is open
      if (dragJustCompleted || activeIndicator) {
        // Close popover
        setActiveIndicator(null);
        setPopoverPosition(null);
        // Clear indicator from URL
        if (onIndicatorChange) {
          onIndicatorChange(null);
        }
        return;
      }
      
      // Additional protection: don't create indicators if currently dragging
      if (isDraggingIndicator()) {
        return;
      }
      
      // VERSION RESTRICTION: Check if this is not the latest version
      if (currentVersion && latestVersion && currentVersion !== latestVersion) {
        if (onVersionRestricted) {
          onVersionRestricted();
        }
        return;
      }
      
      // IRONCLAD VALIDATION: Create pending indicator (not sent to server)
      const pendingInd = {
        x: Math.round(imageX),  // Round to integer to avoid zod schema conflicts
        y: Math.round(imageY),
        canvasX: viewportCoords.x,  // VIEWPORT coords for popover positioning
        canvasY: viewportCoords.y,
        number: pageIndicators.length + 1
      };
      
      setPendingIndicator(pendingInd);
      setActiveIndicator(null); // Clear any existing active indicator
      setPopoverPosition({ x: viewportCoords.x, y: viewportCoords.y });
    };

    const interval = setInterval(checkPointerClick, 16); // Check at ~60fps
    return () => clearInterval(interval);
  }, [handlePointerClick, getIndicatorAtPosition, imageToCanvasCoords, canvasToImageCoords, activeIndicator, dragJustCompleted, isDraggingIndicator, pageIndicators.length, viewMode, onIndicatorChange, setPendingIndicator, setActiveIndicator, setPopoverPosition, currentVersion, latestVersion, onVersionRestricted]);
}